/**
 * Pure H OS · System-of-Record · Audit Trail block.
 *
 * Reads /api/pureh/audit for a given collection+recordId and renders a
 * compact timeline. Read-only. Uses existing primitives only.
 */
import { useEffect, useState } from "react";
import { api } from "../services/api";

type AuditEvent = {
  id: string;
  atRelative: string;
  actor: string;
  actorRole?: string;
  action: string;
  comment?: string;
};

export function AuditTrail({
  collection,
  recordId,
  testId,
}: {
  collection: string;
  recordId: string;
  testId?: string;
}) {
  const [rows, setRows] = useState<AuditEvent[] | null>(null);
  useEffect(() => {
    let alive = true;
    api.audit(collection, recordId, 50)
      .then((r) => { if (alive) setRows(r as AuditEvent[]); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [collection, recordId]);

  if (rows === null) return null;
  if (rows.length === 0) {
    return (
      <div data-testid={testId} className="mt-4">
        <div
          className="text-[10px] uppercase tracking-[0.14em] mb-2"
          style={{ color: "var(--ph-ink-muted)" }}
        >
          Audit trail
        </div>
        <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>
          No audit events yet.
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testId} className="mt-4">
      <div
        className="text-[10px] uppercase tracking-[0.14em] mb-2"
        style={{ color: "var(--ph-ink-muted)" }}
      >
        Audit trail · {rows.length} event{rows.length === 1 ? "" : "s"}
      </div>
      <div className="space-y-2">
        {rows.map((e) => (
          <div
            key={e.id}
            data-testid={`${testId}-${e.id}`}
            className="text-xs p-2.5"
            style={{
              background: "var(--ph-surface)",
              borderRadius: "var(--ph-radius-sm)",
              borderLeft: "2px solid var(--ph-brand-primary)",
            }}
          >
            <div className="flex items-center justify-between">
              <span
                className="font-mono font-semibold"
                style={{ color: "var(--ph-ink-strong)" }}
              >
                {e.action}
              </span>
              <span style={{ color: "var(--ph-ink-muted)" }}>{e.atRelative}</span>
            </div>
            <div className="mt-1" style={{ color: "var(--ph-ink-muted)" }}>
              {e.actor}
              {e.actorRole ? ` · ${e.actorRole}` : ""}
              {e.comment ? ` · ${e.comment}` : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
